import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiClock, FiUser, FiPhone, FiMail, FiMessageSquare, FiCheck, FiInfo } = FiIcons;

const CounsellingForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    consultationType: '',
    previousExperience: '',
    urgency: '',
    goals: '',
    questions: '',
    budget: '',
    howDidYouHear: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Counselling form submitted:', formData);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="pt-16 bg-gradient-to-br from-[#F9FFF5] to-[#EAF8EC] min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div 
            {...fadeInUp}
            className="text-center bg-white rounded-2xl shadow-lg p-12"
          >
            <div className="bg-[#2E7D32] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheck} className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#0B1320] font-poppins mb-4">
              Consultation Booked Successfully!
            </h1>
            <p className="text-xl text-[#4A4A4A] mb-8">
              Thank you for booking your consultation with Sam's Nutri Hub. 
              Our team will contact you within 24 hours to confirm your appointment.
            </p>
            
            <div className="bg-[#EAF8EC] rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-[#0B1320] mb-4">What to Expect:</h3>
              <ul className="text-left text-[#4A4A4A] space-y-2">
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-[#2E7D32] mt-0.5" />
                  <span>Confirmation call/email within 24 hours</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-[#2E7D32] mt-0.5" />
                  <span>Pre-consultation questionnaire (if applicable)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-[#2E7D32] mt-0.5" />
                  <span>Personalized nutrition assessment</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-[#2E7D32] mt-0.5" />
                  <span>Customized protocol recommendations</span>
                </li>
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setIsSubmitted(false)}
                className="bg-[#2E7D32] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#205C25] transition-colors duration-200"
              >
                Book Another Session
              </button>
              <a
                href="/"
                className="border-2 border-[#2E7D32] text-[#2E7D32] px-8 py-3 rounded-lg font-medium hover:bg-[#2E7D32] hover:text-white transition-colors duration-200 text-center"
              >
                Return to Home
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 bg-gradient-to-br from-[#F9FFF5] to-[#EAF8EC] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Header */}
        <motion.div {...fadeInUp} className="text-center mb-12">
          <div className="inline-block mb-4">
            <span className="bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-medium">
              Book Consultation
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-4">
            Personal Nutrition Consultation
          </h1>
          <p className="text-xl text-[#4A4A4A]">
            Schedule a one-on-one session with our expert nutritionists to create your personalized wellness plan
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div>
              <h3 className="text-xl font-bold text-[#0B1320] font-poppins mb-6 flex items-center">
                <SafeIcon icon={FiUser} className="h-5 w-5 mr-2 text-[#2E7D32]" />
                Personal Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-[#0B1320] mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>

            {/* Scheduling */}
            <div>
              <h3 className="text-xl font-bold text-[#0B1320] font-poppins mb-6 flex items-center">
                <SafeIcon icon={FiCalendar} className="h-5 w-5 mr-2 text-[#2E7D32]" />
                Preferred Schedule
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Preferred Time *
                  </label>
                  <select
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  >
                    <option value="">Select Time</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                    <option value="6:00 PM">6:00 PM</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Consultation Type *
                  </label>
                  <select
                    name="consultationType"
                    value={formData.consultationType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  >
                    <option value="">Select Type</option>
                    <option value="online">Online Video Call</option>
                    <option value="phone">Phone Call</option>
                    <option value="in-person">In-Person (Ahmednagar)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Urgency Level *
                  </label>
                  <select
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  >
                    <option value="">Select Urgency</option>
                    <option value="routine">Routine (Within 1 week)</option>
                    <option value="moderate">Moderate (Within 3 days)</option>
                    <option value="urgent">Urgent (Within 24 hours)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Health & Goals */}
            <div>
              <h3 className="text-xl font-bold text-[#0B1320] font-poppins mb-6 flex items-center">
                <SafeIcon icon={FiMessageSquare} className="h-5 w-5 mr-2 text-[#2E7D32]" />
                Health & Goals Information
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Previous Nutrition/Fitness Experience
                  </label>
                  <select
                    name="previousExperience"
                    value={formData.previousExperience}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  >
                    <option value="">Select Experience</option>
                    <option value="none">No previous experience</option>
                    <option value="basic">Basic (Self-guided)</option>
                    <option value="intermediate">Intermediate (Some coaching)</option>
                    <option value="advanced">Advanced (Multiple programs)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Primary Goals & Challenges *
                  </label>
                  <textarea
                    name="goals"
                    value={formData.goals}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                    placeholder="Describe your health goals, current challenges, and what you hope to achieve through our consultation..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Specific Questions or Concerns
                  </label>
                  <textarea
                    name="questions"
                    value={formData.questions}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                    placeholder="Any specific questions you'd like to discuss during the consultation?"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h3 className="text-xl font-bold text-[#0B1320] font-poppins mb-6">
                Additional Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    Budget Range (Monthly) *
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  >
                    <option value="">Select Budget</option>
                    <option value="< ₹10,000">Less than ₹10,000</option>
                    <option value="₹10,000 - ₹25,000">₹10,000 - ₹25,000</option>
                    <option value="₹25,000 - ₹50,000">₹25,000 - ₹50,000</option>
                    <option value="₹50,000+">₹50,000+</option>
                    <option value="flexible">Flexible based on value</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#0B1320] mb-2">
                    How did you hear about us?
                  </label>
                  <select
                    name="howDidYouHear"
                    value={formData.howDidYouHear}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  >
                    <option value="">Select Source</option>
                    <option value="google">Google Search</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="referral">Friend/Family Referral</option>
                    <option value="youtube">YouTube</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                className="w-full bg-[#2E7D32] text-white py-4 px-6 rounded-lg font-semibold hover:bg-[#205C25] transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiCalendar} className="h-5 w-5" />
                <span>Book Consultation</span>
              </button>
            </div>
          </form>
        </motion.div>

        {/* Info Boxes */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div 
            {...fadeInUp}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiInfo} className="h-6 w-6 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Consultation Details</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Duration: 45-60 minutes</li>
                  <li>• Comprehensive health assessment</li>
                  <li>• Personalized recommendations</li>
                  <li>• Follow-up action plan</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div 
            {...fadeInUp}
            className="bg-green-50 border border-green-200 rounded-lg p-6"
          >
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiClock} className="h-6 w-6 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-900 mb-2">24-Hour Promise</h3>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>• Confirmation within 24 hours</li>
                  <li>• Flexible rescheduling options</li>
                  <li>• Pre-consultation materials</li>
                  <li>• Multiple communication channels</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CounsellingForm;